/**
 * Rate Limiter for VesselMind
 * 
 * Protects against API abuse and runaway costs.
 * All rate limiting is isolated to VesselMind module.
 */

class RateLimiter {
  constructor(config = {}) {
    this.windowMs = config.windowMs || 60000; // 1 minute default
    this.maxRequests = config.maxRequests || 20; // 20 requests per window
    this.blockDuration = config.blockDuration || 300000; // 5 minutes
    this.requests = new Map(); // user -> array of timestamps
    this.blocked = new Map(); // user -> block expiry timestamp
    this.concurrent = 0; // Current concurrent requests
    this.maxConcurrent = config.maxConcurrent || 5;
  }

  /**
   * Check if user can make a request
   * @param {string} userId - User identifier (IP or user ID)
   * @returns {object} Result with allowed status and reason
   */
  checkLimit(userId) {
    const now = Date.now();

    // Check if user is blocked
    if (this.blocked.has(userId)) {
      const blockExpiry = this.blocked.get(userId);
      if (now < blockExpiry) {
        const remainingMs = blockExpiry - now;
        const remainingSec = Math.ceil(remainingMs / 1000);
        return {
          allowed: false,
          reason: 'rate_limit_blocked',
          message: `You are temporarily blocked for ${remainingSec} seconds due to repeated violations.`,
          retryAfter: remainingSec
        };
      } else {
        // Block expired, remove it
        this.blocked.delete(userId);
      }
    }

    // Check concurrent requests
    if (this.concurrent >= this.maxConcurrent) {
      return {
        allowed: false,
        reason: 'server_busy',
        message: 'Server is busy processing other requests. Please try again shortly.',
        retryAfter: 5
      };
    }

    // Get user's request history
    if (!this.requests.has(userId)) {
      this.requests.set(userId, []);
    }

    const userRequests = this.requests.get(userId);

    // Remove old requests outside the window
    const windowStart = now - this.windowMs;
    const recentRequests = userRequests.filter(timestamp => timestamp > windowStart);
    this.requests.set(userId, recentRequests);

    // Check if user has exceeded limit
    if (recentRequests.length >= this.maxRequests) {
      // Block the user
      this.blocked.set(userId, now + this.blockDuration);
      
      console.warn(`⚠️ User ${userId} blocked for ${this.blockDuration / 1000}s (exceeded ${this.maxRequests} requests in ${this.windowMs / 1000}s)`);
      
      return {
        allowed: false,
        reason: 'rate_limit_exceeded',
        message: `Too many requests. You are blocked for ${this.blockDuration / 1000} seconds.`,
        retryAfter: this.blockDuration / 1000
      };
    }

    // Allow the request
    return {
      allowed: true,
      remaining: this.maxRequests - recentRequests.length - 1,
      resetIn: Math.ceil((windowStart + this.windowMs - now) / 1000)
    };
  }

  /**
   * Record a request
   * @param {string} userId - User identifier
   */
  recordRequest(userId) {
    const now = Date.now();
    if (!this.requests.has(userId)) {
      this.requests.set(userId, []);
    }
    this.requests.get(userId).push(now);
  }

  /**
   * Increment concurrent request count
   * @returns {boolean} Whether request was allowed
   */
  incrementConcurrent() {
    if (this.concurrent >= this.maxConcurrent) {
      return false;
    }
    this.concurrent++;
    return true;
  }

  /**
   * Decrement concurrent request count
   */
  decrementConcurrent() {
    if (this.concurrent > 0) {
      this.concurrent--;
    }
  }

  /**
   * Get current stats
   * @returns {object} Current limiter stats
   */
  getStats() {
    return {
      totalUsers: this.requests.size,
      blockedUsers: this.blocked.size,
      concurrentRequests: this.concurrent,
      maxConcurrent: this.maxConcurrent,
      maxRequestsPerWindow: this.maxRequests,
      windowMs: this.windowMs
    };
  }

  /**
   * Clear all data (admin function)
   */
  reset() {
    this.requests.clear();
    this.blocked.clear();
    this.concurrent = 0;
    console.log('🔄 Rate limiter reset');
  }
}

// Create singleton instance with default config
const rateLimiter = new RateLimiter({
  windowMs: 60000,      // 1 minute
  maxRequests: 20,      // 20 requests per minute
  blockDuration: 300000, // 5 minutes block
  maxConcurrent: 5      // 5 concurrent requests max
});

/**
 * Check if request is allowed
 * @param {string} userId - User identifier
 * @returns {object} Check result
 */
export const checkRateLimit = (userId) => {
  return rateLimiter.checkLimit(userId);
};

/**
 * Record a request
 * @param {string} userId - User identifier
 */
export const recordRequest = (userId) => {
  rateLimiter.recordRequest(userId);
};

/**
 * Track concurrent request start
 * @returns {boolean} Whether request was allowed
 */
export const startRequest = () => {
  return rateLimiter.incrementConcurrent();
};

/**
 * Track concurrent request end
 */
export const endRequest = () => {
  rateLimiter.decrementConcurrent();
};

/**
 * Get rate limiter stats
 * @returns {object} Stats
 */
export const getRateLimitStats = () => {
  return rateLimiter.getStats();
};

/**
 * Reset rate limiter (admin only)
 */
export const resetRateLimiter = () => {
  rateLimiter.reset();
};

export default rateLimiter;

